import { IconButton, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { colors } from 'styles/colors';
import BlockIcon from '@mui/icons-material/Block';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomDialog from 'components/dialogs/CustomDialog';
import { useBlockDevice, useUnblockDevice } from 'hooks/useDevices';
import { snackbarService } from 'services/snackbarService';
import { getErrorMessage } from 'common/utils';

interface Props {
  refetch?: () => void;
  device: { id: string; blockedAt?: string | null; brand?: string; model?: string };
}

export default function BlockDeviceActionButton({ refetch, device }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const isBlocked = !!device.blockedAt;
  const name = [device.brand, device.model].filter(Boolean).join(' ') || 'this device';

  const close = () => {
    setOpen(false);
    setReason('');
  };

  const { mutate: blockMutation, isLoading: isBlocking } = useBlockDevice(
    () => {
      refetch?.();
      snackbarService.showSnackbar('Device has been blocked successfully', 'success');
      close();
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
      close();
    }
  );

  const { mutate: unblockMutation, isLoading: isUnblocking } = useUnblockDevice(
    () => {
      refetch?.();
      snackbarService.showSnackbar('Device has been unblocked successfully', 'success');
      close();
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
      close();
    }
  );

  const handleConfirm = () => {
    isBlocked ? unblockMutation(device.id) : blockMutation({ id: device.id, reason: reason.trim() || undefined });
  };

  return (
    <>
      <Tooltip title={isBlocked ? 'Unblock' : 'Block'}>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            width: 25,
            height: 25,
            backgroundColor: isBlocked ? colors.success : colors.error,
            borderRadius: '4px',
            padding: 0,
            '&:hover': {
              backgroundColor: isBlocked ? colors.success : colors.delete,
            },
          }}
        >
          <BlockIcon sx={{ fontSize: 15, color: colors.white }} />
        </IconButton>
      </Tooltip>

      <CustomDialog
        open={open}
        title={`${isBlocked ? 'Unblock' : 'Block'} device`}
        message={isBlocked
          ? `Are you sure you want to unblock ${name}?`
          : `Are you sure you want to block ${name}? Every account on it will be refused.`}
        // Why, for the next admin who looks at it.
        content={!isBlocked && (
          <TextField
            fullWidth
            size='small'
            label='Reason (optional)'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 2 }}
          />
        )}
        buttons={[
          <CustomButton
            key='cancel'
            variant='outlined'
            text='Cancel'
            onClick={close}
            disabled={isUnblocking || isBlocking}
            height={35}
          />,
          <CustomButton
            key='block'
            variant='filled'
            color={isBlocked ? 'success' : 'error'}
            text={isBlocked ? 'Unblock' : 'Block'}
            onClick={handleConfirm}
            loading={isUnblocking || isBlocking}
            height={35}
          />,
        ]}
      />
    </>
  );
}
